import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCloseInvoiceUploadModal, setOpenInvoiceDataStoreModal, setOpenInvoiceModal } from "../../redux/features/invoice/invoiceSlice";
import { errorMessage } from "../../utlis/common";
import "./style.css";
import * as XLSX from "xlsx";
import { MESSAGES } from "../../utlis/messages";
import { useState } from "react";



const InvoiceUploadModal = () => {
    const dispatch: any = useDispatch();
    const [loading, setLoading] = useState(false)
    const { openInvoiceUploadModal, editInvoiceData } = useSelector(
        (state: any) => state.invoiceSlice
    );

    // Holds the file/workbook while we wait for the user to pick a sheet (multi-sheet XLSX)
    const [pendingWorkbook, setPendingWorkbook] = useState<XLSX.WorkBook | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingFileInput, setPendingFileInput] = useState<HTMLInputElement | null>(null);
    const [sheetChoices, setSheetChoices] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState<string>("");

    const handleClose = () => {
        dispatch(setCloseInvoiceUploadModal());
    };

    // Shared validation + dispatch logic for CSV/XML/single-sheet XLSX and after a sheet is chosen
    const finalizeUpload = async (data: any[], headers: string[], file: File, fileInput: HTMLInputElement) => {
        console.log("Validated Data:", data);

        if (data.length === 0) {
            errorMessage("No valid data found after validation.");
            fileInput.value = "";
            setLoading(false);
            return;
        }

        const invalidRecords = data.filter((record) => {
            const prezzo = parseFloat(record["prezzo vendita"] || record["Prezzo Vendita"] || 0);
            const costo = parseFloat(record["costo articoli"] || record["Costo Articoli"] || 0);
            const iva = parseFloat(record["iva"] || record["IVA"] || 0);

            return prezzo > 10000 || costo > 10000 || iva > 30;
        });

        if (invalidRecords.length > 0) {
            errorMessage(
                `Some records exceed allowed limits: Prezzo/Costo > 10,000 or IVA > 30. Please correct and re-upload.`
            );
            fileInput.value = "";
            setLoading(false);
            return;
        }
        try {
            dispatch(setCloseInvoiceUploadModal());
            await dispatch(setOpenInvoiceModal({ data, headers, rawFile: file }));
        } catch (error) {
            console.error("Failed to upload inventory CSV:", error);
        }
        finally {
            setLoading(false);
            fileInput.value = "";
        }
    };

    const handleConfirmSheetSelection = async () => {
        if (!pendingWorkbook || !pendingFile || !pendingFileInput) return;

        const worksheet = pendingWorkbook.Sheets[selectedSheet];
        const jsonData: any = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData.length) {
            errorMessage("Selected sheet is empty or invalid");
            return;
        }

        const headers = Object.keys(jsonData[0]);
        const file = pendingFile;
        const fileInput = pendingFileInput;

        setLoading(true);
        setPendingWorkbook(null);
        setPendingFile(null);
        setPendingFileInput(null);
        setSheetChoices([]);

        await finalizeUpload(jsonData, headers, file, fileInput);
    };

    const handleCancelSheetSelection = () => {
        if (pendingFileInput) pendingFileInput.value = "";
        setPendingWorkbook(null);
        setPendingFile(null);
        setPendingFileInput(null);
        setSheetChoices([]);
    };

    const handleFileUpload = (event) => {
        const fileInput = event.target;
        const file = fileInput.files[0];

        if (!file) {
            errorMessage("No file selected");
            return;
        }

        const fileExtension = file.name.split(".").pop().toLowerCase();
        const reader = new FileReader();

        setLoading(true);

        reader.onload = async ({ target }) => {
            if (!target?.result) {
                errorMessage("FileReader failed to read the file");
                return;
            }

            let data: any[] = [];
            let headers: string[] = [];

            if (fileExtension === "csv") {
                const text = target.result as string;
                const rows = text.split(/\r?\n/).map(row => row.trim()).filter(row => row.length > 0);

                if (rows.length === 0) {
                    errorMessage("CSV is empty!");
                    fileInput.value = "";
                    return;
                }

                const delimiter = rows[0].includes(";") ? ";" : ",";

                headers = rows[0]?.split(delimiter).map(header => header.replace(/(^"|"$)/g, "").trim());
                if (!headers || headers.length === 0) {
                    errorMessage("CSV Headers not found!");
                    fileInput.value = "";
                    return;
                }

                for (let i = 1; i < rows.length; i++) {
                    const values = rows[i].split(delimiter).map(value => value.replace(/(^"|"$)/g, "").trim());
                    const record = headers.reduce((obj, header, index) => {
                        obj[header] = isNaN(Number(values[index])) ? values[index] : Number(values[index]);
                        return obj;
                    }, {});

                    data.push(record);
                }
            }
            else if (fileExtension === "xml") {
                const parser = new DOMParser();
                const xmlString = target.result as string;
                const xmlDoc = parser.parseFromString(xmlString, "application/xml");
                const items = xmlDoc.getElementsByTagName("item");
                if (items.length === 0) {
                    errorMessage("Invalid XML format!");
                    fileInput.value = "";
                    return;
                }

                headers = Array.from(items).reduce((acc, item) => {
                    const itemHeaders = Array.from(item.children).map(el => el.tagName);
                    itemHeaders.forEach(header => {
                        if (!acc.includes(header)) {
                            acc.push(header);
                        }
                    });
                    return acc;
                }, [] as string[]);

                if (headers.length === 0) {
                    errorMessage("No headers found in XML items!");
                    fileInput.value = "";
                    return;
                }

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    const record: { [key: string]: any } = {};

                    headers.forEach(header => {
                        record[header] = null;
                    });

                    const childElements = item.children;
                    for (let j = 0; j < childElements.length; j++) {
                        const element = childElements[j];
                        const tagName = element.tagName;
                        const value = element.textContent?.trim() || null;
                        record[tagName] = value && !isNaN(Number(value)) ? Number(value) : value;
                    }
                    data.push(record);
                }
            }
            else if (fileExtension === "xlsx") {
                const binaryStr = target.result as string;
                const workbook = XLSX.read(binaryStr, { type: "binary" });

                if (workbook.SheetNames.length > 1) {
                    // Multiple sheets found: ask the user which one to use instead of silently
                    // reading the first one (this caused a mismatched-total support case)
                    setPendingWorkbook(workbook);
                    setPendingFile(file);
                    setPendingFileInput(fileInput);
                    setSheetChoices(workbook.SheetNames);
                    setSelectedSheet(workbook.SheetNames[0]);
                    setLoading(false);
                    return;
                }

                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData: any = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

                if (!jsonData.length) {
                    errorMessage("XLSX file is empty or invalid");
                    fileInput.value = "";
                    setLoading(false);
                    return;
                }

                headers = Object.keys(jsonData[0]);
                data = jsonData;
            }
            else {
                errorMessage("Unsupported file type. Please upload a CSV, XLSX, or XML file.");
                fileInput.value = "";
                setLoading(false);
                return;
            }

            await finalizeUpload(data, headers, file, fileInput);
        };

        if (fileExtension === "xlsx") {
            reader.readAsBinaryString(file);
        } else {
            reader.readAsText(file);
        }
        // reader.readAsText(file);
    };
    const handleBack = () => {
        dispatch(setCloseInvoiceUploadModal());
        dispatch(setOpenInvoiceDataStoreModal({}));
    };

    return (
        <Modal show={openInvoiceUploadModal} onHide={handleClose}>
            <Modal.Header className="invoice-header" closeButton>
                <Button onClick={handleBack} className="me-2">
                    ← Back
                </Button>
                <Modal.Title>{MESSAGES.UPLOAD_INVOICE}</Modal.Title>

            </Modal.Header>


            <Modal.Body className="invoice-body">
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-4">
                        <Spinner animation="border" role="status" />
                        <span className="mt-2">Loading...</span>
                    </div>
                ) : sheetChoices.length > 0 ? (
                    <div className="d-flex flex-column gap-3 py-2">
                        <p className="mb-1">
                            This file contains <strong>{sheetChoices.length} sheets/tabs</strong>. Please select the one that contains the data for this invoice:
                        </p>
                        <select
                            className="form-select"
                            value={selectedSheet}
                            onChange={(e) => setSelectedSheet(e.target.value)}
                        >
                            {sheetChoices.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={handleCancelSheetSelection}>Cancel</Button>
                            <Button variant="primary" onClick={handleConfirmSheetSelection}>Use this sheet</Button>
                        </div>
                    </div>
                ) : (
                    <div
                        className="upload-area"
                        onClick={() => document.getElementById("csvUpload")?.click()}
                        onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file) {
                                const fakeEvent = {
                                    target: { files: [file] },
                                };
                                handleFileUpload(fakeEvent);
                            }
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "copy";
                        }}
                    >
                        <input
                            type="file"
                            accept=".csv, .xml, .xlsx"
                            id="csvUpload"
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                        />
                        <p className="upload-button">Select File</p>
                        <p className="upload-hint">* Drop or Select File (CSV, XML, XLSX)</p>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default InvoiceUploadModal;
